import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

// Cast import.meta to any to avoid TS complaints in environments without Vite types
const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface AccountMaterial {
  id: string;
  accountId: string;
  formulaId: string;
  formulaCode: string;
  formulaName: string;
  unit: string;
  volatility: number | null;
  volatilityMean: number | null;
  volatilityUpdatedAt: string | null;
  clientCode: string | null;
  isFavorite: boolean;
  instrumentId: number | null;
  quoteSourceSymbol: string | null;
  currentPrice: number | null;
  quoteCurrency: string | null;
  quoteUnit: string | null;
  tradingCalendar: number[] | null;
  contractType?: string | null; // Contract type: 'material', 'ratio', 'FX'
  riskTolerance?: {
    dangerMinus: number;
    warnMinus: number;
    warnPlus: number;
    dangerPlus: number;
  } | null;
  // Strategy settings from settings JSONB
  bufferStrategy?: number;
  logisticsStrategy?: number[];
  isPercentStrategy?: boolean;
  covRatioStrategy?: number[];
  covRatioIsPercent?: boolean;
  isPriceWatchOnly?: boolean;
}

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  // Fast path: user already available
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function getAccountMaterials(accountId: string): Promise<AccountMaterial[]> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/accounts/${accountId}/formulas`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch account formulas');
  }

  const data = await response.json();
  return data.formulas as AccountMaterial[];
}
